import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getStoredCoords,
  clearStoredCoords,
  requestBrowserLocation,
  reverseGeocode,
  matchLocation,
  forwardGeocode,
} from "@/lib/geolocation";

const AUTO_KEY = "taskratel:auto-location";
const LABEL_KEY = "taskratel:location-label";

export interface UserCoords {
  latitude: number;
  longitude: number;
}

interface UserLocationState {
  coords: UserCoords | null;
  label: string | null;
  countryId: string | null;
  stateId: string | null;
  lgaId: string | null;
  area: string | null;
  isDetecting: boolean;
  autoDetect: boolean;
  setAutoDetect: (v: boolean) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<UserLocationState | undefined>(undefined);

export function isAutoLocationEnabled() {
  return localStorage.getItem(AUTO_KEY) !== "0";
}

export function UserLocationProvider({ children }: { children: ReactNode }) {
  const [autoDetect, setAutoDetectState] = useState<boolean>(() => isAutoLocationEnabled());
  const [coords, setCoords] = useState<UserCoords | null>(() => {
    const s = getStoredCoords();
    return s ? { latitude: s.latitude, longitude: s.longitude } : null;
  });
  const [label, setLabel] = useState<string | null>(() => localStorage.getItem(LABEL_KEY));
  const [stateId, setStateId] = useState<string | null>(null);
  const [countryId, setCountryId] = useState<string | null>(null);
  const [lgaId, setLgaId] = useState<string | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);

  const detect = useCallback(async (force = false, background = false) => {
    if (!isAutoLocationEnabled() && !force) return;
    setIsDetecting(true);
    try {
      // Background (non-user-initiated) checks must never trigger the browser
      // permission prompt — only use GPS if permission was already granted.
      if (background) {
        const state = await navigator.permissions
          ?.query({ name: "geolocation" as PermissionName })
          .then((p) => p.state)
          .catch(() => "prompt");
        if (state !== "granted") throw new Error("not-granted");
      }
      const c = await requestBrowserLocation(force);
      // Only update state when the position actually moved (~>150 m), so we
      // don't re-geocode on every poll.
      setCoords((prev) => {
        if (
          prev &&
          Math.abs(prev.latitude - c.latitude) < 0.0015 &&
          Math.abs(prev.longitude - c.longitude) < 0.0015
        ) {
          return prev;
        }
        return { latitude: c.latitude, longitude: c.longitude };
      });

    } catch {
      // Permission denied or GPS unavailable — fall back so the app still works:
      // 1) last known stored fix, 2) city-based estimate from the stored label.
      const stored = getStoredCoords();
      if (stored) {
        setCoords((prev) =>
          prev ?? { latitude: stored.latitude, longitude: stored.longitude },
        );
      } else {
        const savedLabel = localStorage.getItem(LABEL_KEY);
        if (savedLabel && savedLabel !== "Nigeria") {
          const est = await forwardGeocode(savedLabel);
          if (est) {
            setCoords((prev) => prev ?? est);
          }
        }
      }
    } finally {
      setIsDetecting(false);
    }
  }, []);

  // Detect on every app load/refresh (fresh fix), then keep it current over time
  useEffect(() => {
    if (!autoDetect) return;
    void detect(true, true);

    // Periodic refresh while the app stays open
    const interval = window.setInterval(() => void detect(true, true), 10 * 60 * 1000);
    // Refresh when the user comes back to the tab
    const onVisible = () => {
      if (document.visibilityState === "visible") void detect(true, true);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDetect]);

  // Keep in sync with coords updated elsewhere
  useEffect(() => {
    const handler = () => {
      const c = getStoredCoords();
      if (c) setCoords({ latitude: c.latitude, longitude: c.longitude });
    };
    window.addEventListener("taskratel:coords-updated", handler);
    return () => window.removeEventListener("taskratel:coords-updated", handler);
  }, []);


  // Reverse-geocode into a friendly label + Nigeria hierarchy
  useEffect(() => {
    if (!coords || !autoDetect) return;
    let cancelled = false;
    (async () => {
      try {
        const geo = await reverseGeocode(coords.latitude, coords.longitude);
        if (cancelled) return;
        const m = matchLocation(geo);
        setCountryId(m.countryId ?? null);
        setStateId(m.stateId ?? null);
        setLgaId(m.lgaId ?? null);
        setArea(m.area ?? null);
        const parts = [geo.suburb || geo.city, geo.county, geo.state, geo.country].filter(
          Boolean,
        ) as string[];
        const nice = Array.from(new Set(parts)).slice(0, 2).join(", ");
        if (nice) {
          setLabel(nice);
          localStorage.setItem(LABEL_KEY, nice);
          window.dispatchEvent(new Event("taskratel:location-updated"));
        }
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coords, autoDetect]);

  const setAutoDetect = useCallback(
    (v: boolean) => {
      localStorage.setItem(AUTO_KEY, v ? "1" : "0");
      setAutoDetectState(v);
      if (!v) {
        clearStoredCoords();
        localStorage.removeItem(LABEL_KEY);
        setCoords(null);
        setLabel(null);
        setCountryId(null);
        setStateId(null);
        setLgaId(null);
        setArea(null);
        window.dispatchEvent(new Event("taskratel:location-updated"));
      } else {
        void detect(true);
      }
    },
    [detect],
  );

  const value = useMemo<UserLocationState>(
    () => ({
      coords,
      label,
      countryId,
      stateId,
      lgaId,
      area,
      isDetecting,
      autoDetect,
      setAutoDetect,
      refresh: () => detect(true),
    }),
    [coords, label, countryId, stateId, lgaId, area, isDetecting, autoDetect, setAutoDetect, detect],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUserLocation() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useUserLocation must be used within UserLocationProvider");
  return ctx;
}
